import React, { useEffect, useState } from "react";
import axios from "axios";
import { TailSpin } from "react-loader-spinner";

import DocumentViewer from "../../components/DocumentViewer";
import { getApi } from "../../utils/api";
import { API_GET_EMPLOYEE_FILE, API_PHASE2_LIST } from "../../config/Endpoints";
import { Eye } from "lucide-react";

function chequeFileNameFromPath(fullPath) {
  const normalized = String(fullPath).replace(/\\/g, "/");
  const seg = normalized.split("/").filter(Boolean);
  return seg.length > 0 ? seg[seg.length - 1] : "Cancelled cheque";
}

function extractBankFromListResponse(res) {
  const payload = res?.data?.data ?? res?.data;
  const content =
    payload?.content ??
    payload?.data ??
    (Array.isArray(payload) ? payload : null);
  const list = Array.isArray(content) ? content : [];
  const first = list[0];
  return first?.bankDetails ?? null;
}

function formatApprovalStatus(status) {
  if (!status) return "-";
  const u = String(status).toUpperCase();
  if (u === "APPROVED") return "Approved";
  if (u === "REJECTED") return "Rejected";
  if (u === "PENDING" || u === "SUBMITTED") return "Pending";
  return status;
}

function BankGridRow({ left, right = null }) {
  return (
    <div className="row mb-1">
      <div className="col-md-3 text-black-50">{left.label}</div>
      <div className="col-md-3 mb-2">{left.value ?? "-"}</div>
      {right ? (
        <>
          <div className="col-md-3 text-black-50">{right.label}</div>
          <div className="col-md-3 mb-2">{right.value ?? "-"}</div>
        </>
      ) : null}
    </div>
  );
}

const ProfileBankDetails = ({ employeeId, accessToken }) => {
  const [loading, setLoading] = useState(true);
  const [bank, setBank] = useState(null);
  const [chequeLoading, setChequeLoading] = useState(false);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [documentType, setDocumentType] = useState("application/octet-stream");
  const [documentName, setDocumentName] = useState("Cancelled cheque");

  useEffect(() => {
    if (!employeeId || !accessToken) {
      setBank(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const url = `${API_PHASE2_LIST}?employeeId=${encodeURIComponent(
      String(employeeId),
    )}&currentPage=1&pageSize=1`;

    getApi(url, accessToken)
      .then((res) => {
        if (res.status === 200) {
          setBank(extractBankFromListResponse(res));
        } else {
          setBank(null);
        }
        setLoading(false);
      })
      .catch(() => {
        setBank(null);
        setLoading(false);
      });
  }, [employeeId, accessToken]);

  useEffect(() => {
    return () => {
      if (documentUrl) {
        URL.revokeObjectURL(documentUrl);
      }
    };
  }, [documentUrl]);

  const handleViewCheque = async () => {
    const path = bank?.cancelledChequePath;
    if (!path || !accessToken) return;

    setChequeLoading(true);
    try {
      const res = await axios.get(API_GET_EMPLOYEE_FILE, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { filepath: path },
        responseType: "blob",
      });

      if (res.status === 200) {
        if (documentUrl) {
          URL.revokeObjectURL(documentUrl);
        }
        const url = URL.createObjectURL(res.data);
        setDocumentUrl(url);
        setDocumentType(res.data.type || "application/octet-stream");
        setDocumentName(chequeFileNameFromPath(path));
        setShowDocumentViewer(true);
      }
    } catch {
      setShowDocumentViewer(false);
    } finally {
      setChequeLoading(false);
    }
  };

  useEffect(() => {
    if (!showDocumentViewer) return;
    document.getElementById("profile-bank-cheque-viewer-trigger")?.click();
  }, [showDocumentViewer]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <TailSpin height="48" width="48" color="#0d6efd" ariaLabel="loading" />
      </div>
    );
  }

  if (!bank) {
    return (
      <div className="fs-4 text-secondary text-center py-4">
        No records available
      </div>
    );
  }

  return (
    <>
      <div className="mt-3 px-4">
        <BankGridRow
          left={{ label: "Beneficiary Name", value: bank.beneficiaryName }}
          right={{ label: "Account Number", value: bank.accountNumber }}
        />
        <BankGridRow
          left={{ label: "Bank Name", value: bank.bankName }}
          right={{ label: "Branch Name", value: bank.branchName }}
        />
        <BankGridRow
          left={{ label: "IFSC Code", value: bank.ifscCode }}
          right={{
            label: "Approval Status",
            value: formatApprovalStatus(bank.approvalStatus),
          }}
        />
        <BankGridRow
          left={{
            label: "Cancelled Cheque",
            value: bank.cancelledChequePath ? (
              <button
                type="button"
                className="action-btn action-btn--view"
                disabled={chequeLoading}
                onClick={handleViewCheque}
              >
                {chequeLoading ? (
                  "Loading…"
                ) : (
                  <>
                    <Eye size={14} aria-hidden />
                    View
                  </>
                )}
              </button>
            ) : (
              "-"
            ),
          }}
        />
      </div>

      <button
        type="button"
        id="profile-bank-cheque-viewer-trigger"
        data-bs-toggle="modal"
        data-bs-target="#staticBackdropDocumentViewer"
        style={{ display: "none" }}
        aria-hidden="true"
      />

      {showDocumentViewer && documentUrl && (
        <DocumentViewer
          url={documentUrl}
          contentType={documentType}
          fileName={documentName}
          onClose={() => {
            setShowDocumentViewer(false);
            setDocumentUrl((prev) => {
              if (prev) URL.revokeObjectURL(prev);
              return null;
            });
          }}
        />
      )}
    </>
  );
};

export default ProfileBankDetails;
